import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ITepDevice } from 'types/tep';
import { actions } from 'core/redux/api/veta/tep/entry-passes';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { close, deactivate, edit, save } from 'assets/icons';
import { entryPassesSelector } from 'core/redux/selectors/tep';

import styles from './styles.module.css';

interface ITepDeviceActionsCellProps {
  device: ITepDevice;
}

const DELETE_ICON_PROPS = { width: 27, height: 27, style: { marginTop: '1px' }, details: deactivate };

export const TepDeviceActionsCell: FC<ITepDeviceActionsCellProps> = ({ device }) => {
  const dispatch = useDispatch();

  const { intent, selected } = useSelector(entryPassesSelector);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [position, setPosition] = useState({ top: 0, left: 0 });

  const isSelected = selected?.id === device.id;
  const isEdit = isSelected && intent === 'edit';
  const isDelete = isSelected && intent === 'delete';

  const onCellClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const updateConfirmPosition = () => {
    if (wrapperRef.current) {
      const { top, left } = wrapperRef.current.getBoundingClientRect();

      setPosition({ top: top + 35, left: left - 75 });
    }
  };

  const onCancel = () => {
    dispatch(actions.select({ entryPass: null }));
    dispatch(actions.intent({ intent: '' }));
  };

  const onEdit = () => {
    dispatch(actions.select({ entryPass: device }));
    dispatch(actions.intent({ intent: 'edit' }));
  };

  const onDeactivate = () => {
    updateConfirmPosition();
    dispatch(actions.select({ entryPass: device }));
    dispatch(actions.intent({ intent: 'delete' }));
  };

  const deactivateDevice = () => {
    dispatch(actions.remove.request({ id: device.id }));
    onCancel();
  };

  const saveDevice = () => {
    if (selected) {
      const { name, uri } = selected.device;

      dispatch(actions.update.request({ id: device.id, name, uri }));
    }
  };

  useEffect(() => {
    updateConfirmPosition();
  }, []);

  return (
    <div ref={wrapperRef} className={styles.wrapper} onClick={onCellClick}>
      {isEdit ? (
        <div className={styles.editButtons}>
          <SvgWrapper width={28} height={28} details={save} onClick={saveDevice} />
          <SvgWrapper width={28} height={28} details={close} onClick={onCancel} color="#79cdd0" />
        </div>
      ) : (
        <div className={styles.defaultButtons}>
          <SvgWrapper width={17.9} height={17.7} details={edit} onClick={onEdit} />
          <SvgWrapper {...DELETE_ICON_PROPS} onClick={onDeactivate} />
        </div>
      )}
      {isDelete && <ConfirmDeleteModal position={position} onCancel={onCancel} deactivateDevice={deactivateDevice} />}
    </div>
  );
};
